import { BehaviorSubject, Subject } from "rxjs";
import { post, get } from "../../../http";
import { localeService } from "../../../locale/dependencyInjection";
import moment from "moment";
import { downloadFile } from "../../../http/shared/utils/downloadFile";

export enum ExportType {
  PPT = "PPT",
  XLSX = "XLSX",
  PDF = "PDF",
}

export enum ExportError {
  Network,
  Server,
  InvalidCredentials,
  NotVerified,
}

type LocalizationDictionary = {
  [x: number]: string;
};

export class ExportService {
  private _baseUrl: string;
  private _error$ = new Subject<ExportError>();
  private _loading$ = new BehaviorSubject<boolean>(false);

  public get error$() {
    return this._error$.asObservable();
  }

  public get loading$() {
    return this._loading$.asObservable();
  }

  public get loading() {
    return this._loading$.value;
  }

  constructor(apiUrl?: string) {
    this._baseUrl = (apiUrl || "api") + "/export";
  }

  async getExportResult(reportDefinition: any, exportType: ExportType) {
    try {
      this._loading$.next(true);

      const url = `${this._baseUrl}/${exportType}`;
      const resp = await post(`${url}`, reportDefinition);
      if (resp.ok) {
        const blob = await resp.blob();
        const fileName = this._getFileName(reportDefinition.names, exportType);
        const navigatorAs = window.navigator as any;
        if (navigatorAs?.msSaveOrOpenBlob) { // for IE
          navigatorAs.msSaveOrOpenBlob(blob, fileName);
        } else { // for non-IE
          downloadFile(blob, fileName);
        }
        return true;
      }

      this._loading$.next(false);
      return false;
    } catch (err) {
      this._loading$.next(false);

      // TODO: Добавить логику обработки ошибок
      // eslint-disable-next-line no-console
      console.error("get export result fail");
      // eslint-disable-next-line no-console
      console.error(err);
      throw err;
    }
  }

  async getExportUsersResult(exportUsersType: ExportType) {
    try {
      this._loading$.next(true);

      const url = `${this._baseUrl}/Users/${exportUsersType.toLowerCase()}`;
      const resp = await get(`${url}`);

      if (resp.ok) {
        const blob = await resp.blob();
        const fileName = this._getFileNameUsers(exportUsersType);
        const navigatorAs = window.navigator as any;
        if (navigatorAs?.msSaveOrOpenBlob) { // for IE
          navigatorAs.msSaveOrOpenBlob(blob, fileName);
        } else { // for non-IE
          downloadFile(blob, fileName);
        }
        return true;
      }

      this._loading$.next(false);
      return false;
    } catch (err) {
      this._loading$.next(false);

      // TODO: Добавить логику обработки ошибок
      // eslint-disable-next-line no-console
      console.error("get export result fail");
      // eslint-disable-next-line no-console
      console.error(err);
      throw err;
    }
  }

  private _getFileName(lcidName: LocalizationDictionary, exportType: ExportType) {
    const name = lcidName[localeService.lcid];
    const extension = this._getFileExtension(exportType);
    const dateTime = this._getFileDateTime();
    const fileName = `${name} ${dateTime}${extension}`;
    return fileName;
  }

  private _getFileNameUsers(exportType: ExportType) {
    const extension = this._getFileExtension(exportType);
    const dateTime = this._getFileUsersDateTime();
    const fileName = `User's List_${dateTime}${extension}`;
    return fileName;
  }

  private _getFileExtension(exportType: ExportType) {
    const fileTypeMap = {
      [ExportType.PDF]: ".pdf",
      [ExportType.PPT]: ".ppt",
      [ExportType.XLSX]: ".xlsx",
    };

    const extension = fileTypeMap[exportType];
    return extension || "";
  }

  private _getFileDateTime() {
    return moment().format("YYYY-MM-DD_hh-mm-ss");
  }

  private _getFileUsersDateTime() {
    return moment().format("YYYY-MM-DD");
  }
}
